import { useState, useRef, useEffect } from 'react';
import { BookMarked, X, Upload, Loader2, Check, ImageIcon, Download, AlertTriangle, ScanLine } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useAppContext } from '@/hooks/useAppContext';
import { getEffectiveBlossomServers } from '@/lib/appBlossom';
import { uploadWithProgress, uploadFileToAllServers, formatBytes } from '@/lib/blossomUpload';
import { extractPdfCover } from '@/lib/pdfCover';
import { KIND_EBOOK } from '@/lib/pressstrKinds';
import type { NostrEvent } from '@nostrify/nostrify';

interface EditEbookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ebook: NostrEvent;
  onSuccess?: () => void;
}

const ACCEPTED_EBOOK_TYPES = ['application/pdf', 'application/epub+zip'];

interface SaveProgress {
  stage: 'idle' | 'uploading-file' | 'uploading-cover' | 'publishing' | 'complete' | 'error';
  message: string;
  progress: number;
  fileLoaded?: number;
  fileTotal?: number;
  error?: string;
}

export function EditEbookDialog({ open, onOpenChange, ebook, onSuccess }: EditEbookDialogProps) {
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const { mutateAsync: publishEvent, isPending: isPublishing } = useNostrPublish();

  const slug = ebook.tags.find(([n]) => n === 'd')?.[1] ?? '';

  const [title, setTitle] = useState('');
  const [authors, setAuthors] = useState<string[]>([]);
  const [authorInput, setAuthorInput] = useState('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [isbn, setIsbn] = useState('');
  const [language, setLanguage] = useState('');
  const [publishedAt, setPublishedAt] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  // File state
  const [ebookFile, setEbookFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState('');
  const [fileMime, setFileMime] = useState('');
  const [fileSha, setFileSha] = useState('');
  const [fileSize, setFileSize] = useState('');

  // Cover state
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState('');
  const [coverAutoExtracted, setCoverAutoExtracted] = useState(false);

  const [saveProgress, setSaveProgress] = useState<SaveProgress>({ stage: 'idle', message: '', progress: 0 });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTitle(ebook.tags.find(([n]) => n === 'title')?.[1] ?? '');
      setAuthors(ebook.tags.filter(([n]) => n === 'author').map(([, v]) => v));
      setSummary(ebook.tags.find(([n]) => n === 'summary')?.[1] ?? '');
      setDescription(ebook.content ?? '');
      setFileUrl(ebook.tags.find(([n]) => n === 'url')?.[1] ?? '');
      setFileMime(ebook.tags.find(([n]) => n === 'm')?.[1] ?? 'application/pdf');
      setFileSha(ebook.tags.find(([n]) => n === 'x')?.[1] ?? '');
      setFileSize(ebook.tags.find(([n]) => n === 'size')?.[1] ?? '');
      setEbookFile(null);
      setCoverUrl(ebook.tags.find(([n]) => n === 'image')?.[1] ?? '');
      setCoverFile(null);
      setCoverPreview(null);
      setCoverAutoExtracted(false);
      setIsbn(ebook.tags.find(([n]) => n === 'isbn')?.[1] ?? '');
      setLanguage(ebook.tags.find(([n]) => n === 'l')?.[1] ?? '');
      setTags(ebook.tags.filter(([n]) => n === 't').map(([, v]) => v));
      setAuthorInput('');
      setTagInput('');
      const pa = ebook.tags.find(([n]) => n === 'published_at')?.[1];
      setPublishedAt(pa ? new Date(parseInt(pa) * 1000).toISOString().slice(0, 10) : '');
      setSaveProgress({ stage: 'idle', message: '', progress: 0 });
    }
  }, [open, ebook]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_EBOOK_TYPES.includes(file.type)) {
      toast({ title: 'Invalid file', description: 'Please select a PDF or EPUB file.', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    const buf = await file.arrayBuffer();
    const cloned = new File([buf], file.name, { type: file.type });
    setEbookFile(cloned);
    e.target.value = '';

    // Auto-extract cover from PDF first page
    if (file.type === 'application/pdf') {
      try {
        const cover = await extractPdfCover(cloned);
        setCoverFile(cover);
        setCoverPreview(URL.createObjectURL(cover));
        setCoverAutoExtracted(true);
      } catch (err) {
        console.warn('Could not extract PDF cover:', err);
      }
    }
  };

  const handleCoverSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { e.target.value = ''; return; }
    const buf = await file.arrayBuffer();
    const cloned = new File([buf], file.name, { type: file.type });
    setCoverFile(cloned);
    setCoverPreview(URL.createObjectURL(cloned));
    setCoverAutoExtracted(false);
    e.target.value = '';
  };

  const addAuthor = () => {
    const a = authorInput.trim();
    if (a && !authors.includes(a)) { setAuthors([...authors, a]); setAuthorInput(''); }
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/^#/, '');
    if (t && !tags.includes(t)) { setTags([...tags, t]); setTagInput(''); }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({ title: 'Missing fields', description: 'Title is required.', variant: 'destructive' });
      return;
    }
    if (!user) return;

    const servers = getEffectiveBlossomServers(config.blossomServerMetadata, config.useAppBlossomServers);
    if ((ebookFile || coverFile) && servers.length === 0) {
      toast({ title: 'No Blossom servers', description: 'Configure Blossom servers in settings.', variant: 'destructive' });
      return;
    }

    setSaveProgress({ stage: ebookFile ? 'uploading-file' : coverFile ? 'uploading-cover' : 'publishing', message: ebookFile ? 'Uploading ebook to Blossom…' : coverFile ? 'Uploading cover image…' : 'Publishing to Nostr…', progress: 10, fileLoaded: ebookFile ? 0 : undefined, fileTotal: ebookFile?.size });

    try {
      let finalUrl = fileUrl;
      let finalMime = fileMime;
      let finalSha = fileSha;
      let finalSize = fileSize;

      if (ebookFile) {
        const result = await uploadWithProgress(ebookFile, servers, user.signer, ({ loaded, total }) => {
          setSaveProgress(prev => ({ ...prev, fileLoaded: loaded, fileTotal: total }));
        });
        finalUrl = result.url;
        finalMime = ebookFile.type;
        finalSha = result.sha256;
        finalSize = String(ebookFile.size);
      }

      let finalCoverUrl = coverFile ? '' : coverUrl;
      if (coverFile) {
        setSaveProgress({ stage: 'uploading-cover', message: 'Uploading cover image…', progress: 80 });
        const result = await uploadFileToAllServers(coverFile, servers, user.signer);
        finalCoverUrl = result.url;
      }

      setSaveProgress({ stage: 'publishing', message: 'Publishing to Nostr…', progress: 92 });

      const eventTags: string[][] = [
        ['d', slug],
        ['title', title.trim()],
        ['url', finalUrl],
        ['m', finalMime],
        ['alt', `Ebook: ${title.trim()}`],
      ];
      if (finalSha) eventTags.push(['x', finalSha]);
      if (finalSize) eventTags.push(['size', finalSize]);
      if (finalCoverUrl) eventTags.push(['image', finalCoverUrl]);
      for (const a of authors) eventTags.push(['author', a]);
      if (summary.trim()) eventTags.push(['summary', summary.trim()]);
      if (language.trim()) eventTags.push(['l', language.trim().toLowerCase()]);
      if (isbn.trim()) eventTags.push(['isbn', isbn.trim()]);
      for (const t of tags) eventTags.push(['t', t]);
      if (publishedAt) {
        const ts = Math.floor(new Date(publishedAt).getTime() / 1000);
        if (!isNaN(ts)) eventTags.push(['published_at', String(ts)]);
      }

      await publishEvent({ kind: KIND_EBOOK, content: description.trim(), tags: eventTags });
      setSaveProgress({ stage: 'complete', message: 'Ebook updated!', progress: 100 });
      toast({ title: 'Ebook updated!' });
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setSaveProgress({ stage: 'error', message: 'Save failed', progress: 0, error: msg });
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const busy = saveProgress.stage !== 'idle' && saveProgress.stage !== 'error' || isPublishing;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookMarked className="w-5 h-5" />
            Edit Ebook
          </DialogTitle>
          <DialogDescription>
            Update this ebook. You can replace the file — a new cover will be auto-extracted from PDFs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Ebook file */}
          <div>
            <Label className="text-sm font-medium">Ebook File (PDF or EPUB)</Label>
            <input ref={fileInputRef} type="file" accept="application/pdf,application/epub+zip" onChange={handleFileSelect} className="hidden" />
            {ebookFile ? (
              <div className="mt-2 flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
                <Download className="w-5 h-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{ebookFile.name}</p>
                  <p className="text-xs text-muted-foreground">{formatBytes(ebookFile.size)} · {ebookFile.type === 'application/epub+zip' ? 'EPUB' : 'PDF'} · new file</p>
                </div>
                <button onClick={() => { setEbookFile(null); if (coverAutoExtracted) { setCoverFile(null); setCoverPreview(null); setCoverAutoExtracted(false); } }} className="p-1 hover:text-destructive shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                <Download className="w-5 h-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground truncate">{fileUrl || 'No file'}</p>
                  <p className="text-xs text-muted-foreground">Current file · {fileMime === 'application/epub+zip' ? 'EPUB' : 'PDF'}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} className="shrink-0">
                  <Upload className="w-3.5 h-3.5 mr-1" />Replace
                </Button>
              </div>
            )}
          </div>

          {/* Cover */}
          <div>
            <Label className="text-sm font-medium">Cover Image</Label>
            <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverSelect} className="hidden" />
            <div className="mt-2 flex items-start gap-4">
              {coverPreview ? (
                <div className="relative w-20 aspect-[2/3] rounded-lg overflow-hidden bg-muted shrink-0">
                  <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                  <button onClick={() => { setCoverFile(null); setCoverPreview(null); setCoverAutoExtracted(false); }} className="absolute top-1 right-1 p-0.5 rounded-full bg-black/60 text-white"><X className="w-3 h-3" /></button>
                </div>
              ) : coverUrl ? (
                <div className="relative w-20 aspect-[2/3] rounded-lg overflow-hidden bg-muted shrink-0 cursor-pointer" onClick={() => coverInputRef.current?.click()}>
                  <img src={coverUrl} alt="Current cover" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <Upload className="w-4 h-4 text-white" />
                  </div>
                </div>
              ) : (
                <button onClick={() => coverInputRef.current?.click()} className="w-20 aspect-[2/3] border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-1 hover:border-primary hover:bg-muted/50 transition-colors shrink-0">
                  <Upload className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
              <div className="flex-1 pt-1 space-y-1">
                {coverAutoExtracted && (
                  <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />Auto-extracted from new PDF
                  </p>
                )}
                <p className="text-xs text-muted-foreground">Replace the cover, or replace the PDF to auto-extract a new one.</p>
                <button onClick={() => coverInputRef.current?.click()} className="text-xs text-primary underline">Upload manually</button>
              </div>
            </div>
          </div>

          {/* Progress bars */}
          {saveProgress.stage !== 'idle' && saveProgress.stage !== 'complete' && saveProgress.stage !== 'error' && (
            <div className="space-y-3 p-3 rounded-lg bg-muted/40">
              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{saveProgress.message}</span>
                  <span className="font-medium">{saveProgress.progress}%</span>
                </div>
                <Progress value={saveProgress.progress} />
              </div>
              {saveProgress.fileTotal !== undefined && saveProgress.fileTotal > 0 && (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Upload className="w-3 h-3" />File upload</span>
                    <span className="tabular-nums">
                      {formatBytes(saveProgress.fileLoaded ?? 0)} / {formatBytes(saveProgress.fileTotal)}
                      {' '}
                      <span className="text-foreground font-medium">
                        ({Math.round(((saveProgress.fileLoaded ?? 0) / saveProgress.fileTotal) * 100)}%)
                      </span>
                    </span>
                  </div>
                  <Progress value={Math.round(((saveProgress.fileLoaded ?? 0) / saveProgress.fileTotal) * 100)} className="h-2" />
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-muted-foreground justify-center">
                <Loader2 className="w-3 h-3 animate-spin" />Please wait…
              </div>
            </div>
          )}

          {saveProgress.stage === 'error' && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{saveProgress.error}</AlertDescription>
            </Alert>
          )}

          <div>
            <Label htmlFor="edit-ebook-title">Title *</Label>
            <Input id="edit-ebook-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" disabled={busy} />
          </div>

          <div>
            <Label>Authors</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={authorInput} onChange={(e) => setAuthorInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAuthor())} placeholder="Author name" disabled={busy} />
              <Button type="button" variant="secondary" onClick={addAuthor} disabled={busy}>Add</Button>
            </div>
            {authors.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {authors.map(a => (
                  <Badge key={a} variant="secondary" className="pl-2">{a}<button onClick={() => setAuthors(authors.filter(x => x !== a))} className="ml-1 hover:text-destructive"><X className="w-3 h-3" /></button></Badge>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="edit-ebook-summary">Short Summary</Label>
            <Input id="edit-ebook-summary" value={summary} onChange={(e) => setSummary(e.target.value)} className="mt-1.5" disabled={busy} />
          </div>
          <div>
            <Label htmlFor="edit-ebook-desc">Full Description (Markdown)</Label>
            <Textarea id="edit-ebook-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="mt-1.5" disabled={busy} />
          </div>
          <div>
            <Label htmlFor="edit-ebook-isbn">ISBN-13</Label>
            <Input id="edit-ebook-isbn" value={isbn} onChange={(e) => setIsbn(e.target.value)} placeholder="978-0000000000" className="mt-1.5 w-48" disabled={busy} />
          </div>
          <div>
            <Label htmlFor="edit-ebook-lang">Language</Label>
            <Input id="edit-ebook-lang" value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="en, de, fr…" className="mt-1.5 w-32" maxLength={5} disabled={busy} />
          </div>
          <div>
            <Label htmlFor="edit-ebook-date">Original Publication Date</Label>
            <Input id="edit-ebook-date" type="date" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} className="mt-1.5 w-48" disabled={busy} />
          </div>
          <div>
            <Label>Topics / Tags</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="bitcoin, fiction…" disabled={busy} />
              <Button type="button" variant="secondary" onClick={addTag} disabled={busy}>Add</Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map(t => (
                  <Badge key={t} variant="secondary" className="pl-2">#{t}<button onClick={() => setTags(tags.filter(x => x !== t))} className="ml-1 hover:text-destructive"><X className="w-3 h-3" /></button></Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSubmit} className="flex-1" disabled={busy || !title.trim()}>
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Check className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
